<template>
    <div class="container min-full d-flex flex-column align-items-center justify-content-start py-4">
        <h1 class="my-4">{{ $t('helpSupportTitle') }}</h1>
        <p class="text-center">{{ $t('helpSupportIntro') }}</p>
        <b-button class="mt-3 mb-5 py-3 px-5" variant="primary link" :href="`mailto:${$store.state.site.supportEmail}`">{{ $store.state.site.supportEmail }}</b-button>
       
        <b-card class="w-100 mb-5" style="max-width: 900px;">
             <h2 class="text-center">{{ assessmentCompatibilityTitle }}</h2>
            <p class="text-center">{{ assessmentCompatibilityIntro }}</p>
            <hr>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div class="mb-3 mb-md-0 pr-md-4">
                    <h4 class="mb-2">{{ assessmentCompatibilityStatusTitle }}</h4>
                    <p class="mb-0 text-muted">{{ assessmentCompatibilityStatusText }}</p>
                </div>
                <b-badge :variant="assessmentBrowserSupport.supported ? 'success' : 'danger'" pill class="px-3 py-2 align-self-start align-self-md-center">
                    {{ assessmentBrowserSupport.supported ? $t('helpSupportCompatible') : $t('helpSupportNotCompatible') }}
                </b-badge>
            </div>
            <b-list-group flush class="mt-4">
                <b-list-group-item v-for="requirement in browserRequirements" :key="requirement.key">
                    <div class="d-flex justify-content-between align-items-start">
                        <div class="pr-3">
                            <h5 class="mb-1">{{ requirement.label }}</h5>
                            <p class="mb-0 text-muted">{{ requirement.description }}</p>
                        </div>
                        <div class="d-flex align-items-center text-nowrap ml-3">
                            <b-icon
                                :icon="requirement.supported ? 'check-circle-fill' : 'exclamation-circle-fill'"
                                :variant="requirement.supported ? 'success' : 'danger'"
                                scale="1.1"
                                class="mr-2"
                            />
                            <strong>{{ requirement.supported ? $t('helpSupportSupported') : $t('helpSupportNotSupported') }}</strong>
                        </div>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </b-card>
        
        <b-card class="w-100" style="max-width: 900px;">
            <h2 class="text-center">{{ $t('helpSupportDebugTitle') }}</h2>
            <p class="text-center">{{ $t('helpSupportDebugText') }}</p>
            <hr>
            <b-card-text style="overflow-y: auto; word-break: break-all; padding: 1rem; border-radius: 0.25rem;">
                <p v-html="debugInfo"></p>
                <p style="white-space: pre-wrap;">{{ $store.state }}</p>
            </b-card-text>
        </b-card>
        <h2 class="mt-5">{{ $t('helpSupportClearCacheTitle') }}</h2>
        <p class="text-center">{{ $t('helpSupportClearCacheText') }}</p>
        <b-button class="mt-3 py-3 px-5" variant="primary" @click="clearCache">{{ $t('helpSupportClearCacheButton') }}</b-button>
    </div>
</template>
<script>
import getAssessmentBrowserSupport from '@/common/getAssessmentBrowserSupport';

export default {
    name: 'HelpSupportScreen',
    computed: {
        assessmentBrowserSupport() {
            return getAssessmentBrowserSupport({
                capturePhotos: true,
                captureScreens: true,
                ai: true,
            });
        },
        assessmentCompatibilityTitle() {
            return this.$t('helpSupportAssessmentCompatibilityTitle');
        },
        assessmentCompatibilityIntro() {
            return this.$t('helpSupportAssessmentCompatibilityIntro');
        },
        assessmentCompatibilityStatusTitle() {
            if (this.assessmentBrowserSupport.supported) {
                return this.$t('helpSupportAssessmentCompatibilityStatusSupportedTitle');
            }

            return this.$t('helpSupportAssessmentCompatibilityStatusUnsupportedTitle');
        },
        assessmentCompatibilityStatusText() {
            if (this.assessmentBrowserSupport.supported) {
                return this.$t('helpSupportAssessmentCompatibilityStatusSupportedText');
            }

            return this.$t('helpSupportAssessmentCompatibilityStatusUnsupportedText');
        },
        browserRequirements() {
            const descriptions = {
                'screen-details': {
                    label: this.$t('helpSupportRequirementScreenDetailsLabel'),
                    description: this.$t('helpSupportRequirementScreenDetailsText'),
                },
                'wake-lock': {
                    label: this.$t('helpSupportRequirementWakeLockLabel'),
                    description: this.$t('helpSupportRequirementWakeLockText'),
                },
                fullscreen: {
                    label: this.$t('fullscreen'),
                    description: this.$t('fullscreenText'),
                },
                permissions: {
                    label: this.$t('helpSupportRequirementPermissionsLabel'),
                    description: this.$t('helpSupportRequirementPermissionsText'),
                },
                'media-devices': {
                    label: this.$t('helpSupportRequirementMediaDevicesLabel'),
                    description: this.$t('helpSupportRequirementMediaDevicesText'),
                },
                'user-media': {
                    label: this.$t('helpSupportRequirementUserMediaLabel'),
                    description: this.$t('helpSupportRequirementUserMediaText'),
                },
                'display-media': {
                    label: this.$t('capture'),
                    description: this.$t('captureText'),
                },
                'image-capture': {
                    label: this.$t('helpSupportRequirementImageCaptureLabel'),
                    description: this.$t('helpSupportRequirementImageCaptureText'),
                },
            };

            return this.assessmentBrowserSupport.checks.map((check) => ({
                ...check,
                ...descriptions[check.key],
            }));
        },
        mobilePromptDebug() {
            const sessionValue = sessionStorage.getItem('is_ms_store_pwa');
            const fromMsStore = document.referrer === 'app-info://platform/microsoft-store' || sessionValue === 'true';
            const isIosDevice = this.$device.ios || this.$device.ipad;
            const isAndroidDevice = this.$device.android || (this.$device.mobile && !isIosDevice);
            const hasAppleAppStore = Boolean(this.$store.state.site.appleAppStore);
            const hasGooglePlayStore = Boolean(this.$store.state.site.googlePlayStore);
            const showMobilePrompt = !fromMsStore && ((isIosDevice && hasAppleAppStore) || (isAndroidDevice && hasGooglePlayStore));

            return {
                showMobilePrompt,
                isIosDevice,
                isAndroidDevice,
                fromMsStore,
                hasAppleAppStore,
                hasGooglePlayStore,
                documentReferrer: document.referrer || '(empty)',
                msStoreSessionValue: sessionValue === null ? '(missing)' : sessionValue,
                device: {
                    mobile: Boolean(this.$device.mobile),
                    android: Boolean(this.$device.android),
                    ipad: Boolean(this.$device.ipad),
                    ios: Boolean(this.$device.ios),
                    windows: Boolean(this.$device.windows),
                },
            };
        },
        debugInfo() {
            const notSupported = this.$t('helpSupportNotSupported');
            const formatBoolean = (value) => value ? this.$t('helpSupportBooleanTrue') : this.$t('helpSupportBooleanFalse');

            return `${this.$t('appVersion')}: ${process.env.VUE_APP_VERSION}<br>
            ${this.$t('helpSupportDebugUserAgent')}: ${navigator.userAgent}<br>
            ${this.$t('helpSupportDebugOnline')}: ${formatBoolean(navigator.onLine)}<br>
            ${this.$t('helpSupportDebugSpeed')}: ${'connection' in navigator ? navigator.connection.downlink : notSupported}<br>
            ${this.$t('helpSupportDebugUseLowData')}: ${'connection' in navigator ? formatBoolean(navigator.connection.saveData) : notSupported}<br>
            ${this.$t('helpSupportDebugMicrosoftStore')}: ${formatBoolean(document.referrer === 'app-info://platform/microsoft-store' || sessionStorage.getItem('is_ms_store_pwa') === 'true')}<br>
            Mobile prompt visible: ${formatBoolean(this.mobilePromptDebug.showMobilePrompt)}<br>
            iOS device match (ios || ipad): ${formatBoolean(this.mobilePromptDebug.isIosDevice)}<br>
            Android device match (android || (mobile && !ios/ipad)): ${formatBoolean(this.mobilePromptDebug.isAndroidDevice)}<br>
            Device.mobile: ${formatBoolean(this.mobilePromptDebug.device.mobile)}<br>
            Device.android: ${formatBoolean(this.mobilePromptDebug.device.android)}<br>
            Device.ipad: ${formatBoolean(this.mobilePromptDebug.device.ipad)}<br>
            Device.ios: ${formatBoolean(this.mobilePromptDebug.device.ios)}<br>
            Device.windows: ${formatBoolean(this.mobilePromptDebug.device.windows)}<br>
            Mobile prompt blocked by MS Store detection: ${formatBoolean(this.mobilePromptDebug.fromMsStore)}<br>
            MS Store referrer: ${this.mobilePromptDebug.documentReferrer}<br>
            MS Store session value: ${this.mobilePromptDebug.msStoreSessionValue}<br>
            Apple App Store URL present: ${formatBoolean(this.mobilePromptDebug.hasAppleAppStore)}<br>
            Google Play URL present: ${formatBoolean(this.mobilePromptDebug.hasGooglePlayStore)}<br>
            Apple App Store URL: ${this.$store.state.site.appleAppStore || '(missing)'}<br>
            Google Play URL: ${this.$store.state.site.googlePlayStore || '(missing)'}`;
        }
    },
    methods: {
        async clearCache() {
            await caches.delete('courses');
            this.$store.dispatch('clearAssessments');
            this.$store.dispatch('clearCourse');
            this.$store.dispatch('emptyRequestQueue');
            window.location.reload();
        }
    }
}
</script>
